import React from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import './ReportChart.css';

/**
 * Dark theme configuration for Nivo charts
 */
const darkTheme = {
  background: 'transparent',
  textColor: '#ffffff',
  fontSize: 12,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'SF Pro', sans-serif",
  axis: {
    domain: { line: { stroke: '#444444' } },
    ticks: { 
      line: { stroke: '#444444' }, 
      text: { fill: '#999999' } 
    },
    legend: { text: { fill: '#cccccc' } }
  },
  grid: { line: { stroke: '#333333' } },
  legends: { text: { fill: '#999999' } },
  tooltip: { 
    container: { 
      background: '#1a1a1a', 
      color: '#ffffff',
      fontSize: 12,
      borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)'
    } 
  }
};

/**
 * Transform timeseries data for Nivo line chart
 * Handles both single-series and multi-series data:
 * - Single series: [{ date: '2026-01-01', value: 150 }, ...]
 * - Multi-series: [{ series: 'v1.0', data: [{ date: '...', value: 1 }] }, ...]
 */
const transformTimeseriesData = (data, chartId) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }
  
  // Check if data is multi-series format (has 'series' and 'data' properties)
  const isMultiSeries = data[0] && 'series' in data[0] && Array.isArray(data[0].data);
  
  if (isMultiSeries) {
    // Multi-series format: transform each series
    return data.map(seriesItem => ({
      id: seriesItem.series || 'Unknown',
      data: (seriesItem.data || []).map(item => ({
        x: item.date || item.x,
        y: item.value ?? item.y ?? 0
      }))
    }));
  }
  
  // Single series format
  return [{
    id: chartId || 'series',
    data: data.map(item => ({
      x: item.date || item.x,
      y: item.value ?? item.y
    }))
  }];
};

/**
 * Transform bar data for Nivo bar chart
 * Expects data like: [{ label: 'Category A', value: 100 }, ...]
 */
const transformBarData = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }
  
  return data.map(item => ({
    label: item.label || item.name || item.category,
    value: item.value ?? item.count ?? 0
  }));
};

/**
 * Transform pie data for Nivo pie chart
 * Expects data like: [{ label: 'Slice A', value: 30 }, ...]
 */
const transformPieData = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }
  
  return data.map(item => ({
    id: item.label || item.name || item.id,
    label: item.label || item.name || item.id,
    value: item.value ?? item.count ?? 0
  }));
};

/**
 * Color palette for multi-series charts
 */
const multiSeriesColors = [
  '#0a84ff', // Blue
  '#30d158', // Green
  '#ff9f0a', // Orange
  '#bf5af2', // Purple
  '#ff375f', // Red
  '#64d2ff', // Cyan
  '#ffd60a', // Yellow
  '#ff6482', // Pink
];

/**
 * Timeseries chart using Nivo ResponsiveLine
 */
// Custom layer that masks the bottom margin so the area fill doesn't bleed onto x-axis labels
const AreaBottomMask = ({ innerWidth, innerHeight, margin }) => (
  <g transform={`translate(${-margin.left}, ${innerHeight})`}>
    <rect
      width={innerWidth + margin.left + margin.right}
      height={margin.bottom}
      style={{ fill: 'var(--chart-bg, #111111)' }}
    />
  </g>
);

const TimeseriesChart = ({ data, id, onClick }) => {
  const chartData = transformTimeseriesData(data, id);

  if (chartData.length === 0 || chartData[0].data.length === 0) {
    return <div className="report-chart-empty">No data available</div>;
  }

  const isMultiSeries = chartData.length > 1;
  const rightMargin = isMultiSeries ? 120 : 20;

  return (
    <ResponsiveLine
      data={chartData}
      theme={darkTheme}
      margin={{ top: 20, right: rightMargin, bottom: 60, left: 60 }}
      xScale={{ type: 'point' }}
      yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
      curve="monotoneX"
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: -45,
        legendOffset: 50,
        legendPosition: 'middle'
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legendOffset: -50,
        legendPosition: 'middle'
      }}
      colors={isMultiSeries ? multiSeriesColors : ['#0a84ff']}
      pointSize={isMultiSeries ? 4 : 6}
      pointColor={{ theme: 'background' }}
      pointBorderWidth={2}
      pointBorderColor={{ from: 'serieColor' }}
      enableGridX={false}
      enableGridY={true}
      useMesh={true}
      onClick={onClick ? (point) => onClick(point.data) : null}
      enableArea={!isMultiSeries}
      areaOpacity={0.15}
      layers={['grid', 'markers', 'areas', AreaBottomMask, 'axes', 'crosshair', 'lines', 'points', 'slices', 'mesh', 'legends']}
      legends={isMultiSeries ? [
        {
          anchor: 'right',
          direction: 'column',
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 4,
          itemDirection: 'left-to-right',
          itemWidth: 90,
          itemHeight: 18,
          itemTextColor: '#999999',
          symbolSize: 10,
          symbolShape: 'circle'
        }
      ] : []}
    />
  );
};

/**
 * Bar chart using Nivo ResponsiveBar
 */
const BarChart = ({ data }) => {
  const chartData = transformBarData(data);
  
  if (chartData.length === 0) {
    return <div className="report-chart-empty">No data available</div>;
  }

  return (
    <ResponsiveBar
      data={chartData}
      theme={darkTheme}
      keys={['value']}
      indexBy="label"
      margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
      padding={0.3}
      valueScale={{ type: 'linear' }}
      indexScale={{ type: 'band', round: true }}
      colors={['#0a84ff']}
      borderRadius={4}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: -45,
        legendOffset: 50,
        legendPosition: 'middle'
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legendOffset: -50,
        legendPosition: 'middle'
      }}
      enableGridY={true}
      enableLabel={false}
    />
  );
};

/**
 * Pie chart using Nivo ResponsivePie
 */
const PieChart = ({ data }) => {
  const chartData = transformPieData(data);
  
  if (chartData.length === 0) {
    return <div className="report-chart-empty">No data available</div>;
  }

  return (
    <ResponsivePie
      data={chartData}
      theme={darkTheme}
      margin={{ top: 20, right: 80, bottom: 20, left: 80 }}
      innerRadius={0.5}
      padAngle={1}
      cornerRadius={4}
      activeOuterRadiusOffset={8}
      colors={{ scheme: 'blues' }}
      borderWidth={1}
      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
      arcLinkLabelsSkipAngle={10}
      arcLinkLabelsTextColor="#cccccc"
      arcLinkLabelsThickness={2}
      arcLinkLabelsColor={{ from: 'color' }}
      arcLabelsSkipAngle={10}
      arcLabelsTextColor="#ffffff"
      legends={[
        {
          anchor: 'right',
          direction: 'column',
          justify: false,
          translateX: 70,
          translateY: 0,
          itemsSpacing: 4,
          itemWidth: 60,
          itemHeight: 18,
          itemTextColor: '#999999',
          itemDirection: 'left-to-right',
          symbolSize: 12,
          symbolShape: 'circle'
        }
      ]}
    />
  );
};

/**
 * Single value display component
 */
const SingleValueChart = ({ data, name }) => {
  // data can be a number or an object with value property
  const value = typeof data === 'number' ? data : (data?.value ?? data?.[0]?.value ?? '—');
  const formattedValue = typeof value === 'number' 
    ? value.toLocaleString() 
    : value;

  return (
    <div className="report-chart-single-value">
      <div className="report-chart-single-value-number">{formattedValue}</div>
      {name && <div className="report-chart-single-value-label">{name}</div>}
    </div>
  );
};

/**
 * Table chart component
 */
const TableChart = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div className="report-chart-empty">No data available</div>;
  }

  // Get columns from first data item
  const columns = Object.keys(data[0]);

  return (
    <div className="report-chart-table-wrapper">
      <table className="report-chart-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              {columns.map(col => (
                <td key={col}>{row[col]?.toLocaleString?.() ?? row[col] ?? '—'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * ReportChart - Wrapper component that renders the correct chart based on type
 * 
 * Props:
 * - id: Chart identifier
 * - name: Chart display name
 * - type: Chart type ('timeseries', 'bar', 'pie', 'single_value', 'table')
 * - data: Chart data (format depends on type)
 */
const ReportChart = ({ id, name, type, data, onClick }) => {
  const renderChart = () => {
    switch (type) {
      case 'timeseries':
        return <TimeseriesChart data={data} id={id} onClick={onClick} />;
      case 'bar':
        return <BarChart data={data} />;
      case 'pie':
        return <PieChart data={data} />;
      case 'single_value':
        return <SingleValueChart data={data} name={name} />;
      case 'table':
        return <TableChart data={data} />;
      default:
        return <div className="report-chart-empty">Unknown chart type: {type}</div>;
    }
  };

  return (
    <div className="report-chart">
      {name && type !== 'single_value' && (
        <div className="report-chart-header">
          <h4 className="report-chart-title">{name}</h4>
        </div>
      )}
      <div className="report-chart-content">
        {renderChart()}
      </div>
    </div>
  );
};

export default ReportChart;
